import { adminHttp } from "@shared/lib/http";

export type BillingPlan = {
  id: string;
  code?: string;
  name: string;
  pricePerMonth: number;
  currency: string;
  features: string[];
  isTrialAllowed?: boolean;
  interval?: string;
  isRecommended?: boolean;
  trialDays?: number;
};

export type SubscriptionInfo = {
  currentPlanId: string | null;
  currentPlanName?: string | null;
  status: string;
  renewsOn?: string | null;
  effectiveOn?: string | null;
  paymentProvider?: string | null;
  id?: string | null;
  amount?: number | null;
  currency?: string | null;
  interval?: string | null;
  periodEndsAtUtc?: string | null;
  trialEndsAtUtc?: string | null;
  renewalMode?: string;
  pendingPayment?: SubscriptionRecord | null;
};

export type SubscriptionRecord = {
  id: string;
  planId: string;
  planName: string | null;
  status: string;
  amount: number | null;
  currency: string | null;
  interval: string | null;
  paymentProvider: string | null;
  createdAtUtc: string;
  effectiveAtUtc: string | null;
  periodEndsAtUtc: string | null;
  renewalMode: string;
  events: { kind: string; atUtc: string }[];
};
export type BillingPage<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};
export type InvoiceFilters = {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  status?: string;
  type?: string;
  currency?: string;
  search?: string;
};

export type Invoice = {
  id: string;
  number: string;
  type: string;
  status: string;
  total: number;
  currency: string;
  issuedAtUtc: string;
  paidAtUtc?: string | null;
  downloadUrl?: string | null;
  paidTotal?: number | null;
  outstanding?: number | null;
  reconciliationRequired?: boolean;
  documentUnavailableReason?: string | null;
  billingPeriodStartUtc?: string | null;
  billingPeriodEndUtc?: string | null;
};

export type UsageInfo = {
  year: number;
  month: number;
  budgetUsd: number | null;
  usedUsd: number;
  usedPercent: number | null;
  status: string;
  isUnlimited: boolean;
  hasRecord: boolean;
  lastRecordedAtUtc: string | null;
  warningThresholdPercent: number;
  criticalThresholdPercent: number;
  blockThresholdPercent: number;
  availablePeriods: { year: number; month: number }[];
  coverage: string;
};

export type PendingPlatformPayment = {
  orderId: string;
  planId: string;
  planName: string;
  status: string;
  provider?: string | null;
  amount: number;
  currency: string;
  reference?: string | null;
  createdAtUtc: string;
};

export type PlatformPaymentOptions = {
  payPalEnabled: boolean;
  payPalClientId?: string | null;
  payPalEnvironment: "sandbox" | "live" | string;
  cashEnabled: boolean;
  cashInstructions: string;
  pendingPayment?: PendingPlatformPayment | null;
};

export type PlatformSubscriptionResult = {
  orderId: string;
  status: string;
  renewsOn?: string | null;
};

// Internal type for backend response to avoid 'any'
type BackendPlan = {
  id: string | number;
  code?: string;
  name: string;
  pricePerMonth?: number;
  currency?: string;
  features?: string[];
  isTrialAllowed?: boolean;
  interval?: string;
  isRecommended?: boolean;
  trialDays?: number;
};

export async function getBillingPlans(): Promise<BillingPlan[]> {
  const res = await adminHttp.get<{ items: BackendPlan[] } | BackendPlan[]>(
    "/api/admin/billing/plans",
  );
  const items = Array.isArray(res) ? res : res?.items || [];
  return items.map((p) => ({
    id: String(p.id),
    code: p.code,
    name: p.name || "Unnamed plan",
    pricePerMonth: Number(p.pricePerMonth ?? 0),
    currency: p.currency || "USD",
    features: Array.isArray(p.features) ? p.features : [],
    isTrialAllowed: Boolean(p.isTrialAllowed),
    trialDays: Number(p.trialDays ?? (p.isTrialAllowed ? 30 : 0)),
    interval: p.interval || "month",
    isRecommended: Boolean(p.isRecommended),
  }));
}

export async function getSubscription(
  tenantId?: string,
): Promise<SubscriptionInfo> {
  const res = await adminHttp.get<SubscriptionInfo>(
    "/api/admin/billing/subscription",
    { tenantId },
  );
  return {
    ...res,
    currentPlanId: res?.currentPlanId ? String(res.currentPlanId) : null,
    currentPlanName: res?.currentPlanName ?? null,
    status: res?.status || "none",
    renewsOn: res?.renewsOn || null,
    effectiveOn: res?.effectiveOn || null,
    paymentProvider: res?.paymentProvider || null,
  };
}

export type PlanLimits = {
  products?: number | null;
  connectors?: number | null;
};

export function parseLimitsFromFeatures(features?: string[]): PlanLimits {
  if (!Array.isArray(features)) return {};
  const limits: PlanLimits = {};
  features.forEach((f) => {
    const lower = String(f || "").toLowerCase();
    const num = Number((lower.match(/(\d+)/) || [])[1]);
    if (Number.isFinite(num)) {
      if (lower.includes("product")) limits.products = num;
      if (lower.includes("connector")) limits.connectors = num;
    }
    if (lower.includes("unlimited")) {
      if (lower.includes("product")) limits.products = null;
      if (lower.includes("connector")) limits.connectors = null;
    }
  });
  return limits;
}

export async function fetchPlanLimitsForTenant(
  tenantId?: string | null,
): Promise<{ storeId: string; plan?: BillingPlan; limits: PlanLimits }> {
  const [plans, subscription] = await Promise.all([
    getBillingPlans(),
    getSubscription(),
  ]);
  const plan = subscription.currentPlanId
    ? plans.find((p) => p.id === subscription.currentPlanId)
    : plans[0];
  const limits = plan ? parseLimitsFromFeatures(plan.features) : {};
  return { storeId: tenantId ? String(tenantId) : "current", plan, limits };
}

export type StoreSummary = {
  id: string;
  name: string;
  slug: string;
  tenantId: string;
  planId?: string | null;
  planName?: string | null;
  subscriptionStatus?: string | null;
  domain?: string | null;
};

type BackendStore = {
  id: string;
  name: string;
  slug: string;
  tenantId: string;
  planId?: string;
  planName?: string;
  subscriptionStatus?: string;
};

export async function listStores(): Promise<StoreSummary[]> {
  const res = await adminHttp.get<{ items: BackendStore[] } | BackendStore[]>(
    "/api/stores",
  );
  const items = Array.isArray(res) ? res : res?.items || [];
  return items.map((s: any) => ({
    id: s.id,
    name: s.name || s.storeName || s.slug || "Unnamed Store",
    slug: s.slug || "",
    tenantId: s.tenantId || s.id,
    planId: s.planId,
    planName: s.planName,
    subscriptionStatus: s.subscriptionStatus,
    domain: s.domain,
  }));
}

export async function getStore(id: string): Promise<StoreSummary> {
  return await adminHttp.get<StoreSummary>(
    `/api/stores/${encodeURIComponent(id)}`,
  );
}

export async function createStoreForUser(input: {
  name: string;
  slug: string;
  adminEmail?: string;
  adminPassword?: string;
}): Promise<StoreSummary> {
  const payload = {
    storeName: input.name,
    storeSlug: input.slug,
    adminEmail: input.adminEmail || "",
    adminPassword: input.adminPassword || "",
  };
  return await adminHttp.post<StoreSummary>("/api/stores", payload);
}

export function billingQuery(params: object) {
  return new URLSearchParams(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}

export async function getInvoices(
  filters: InvoiceFilters = {},
  tenantId?: string,
): Promise<BillingPage<Invoice>> {
  return adminHttp.get(`/api/admin/billing/invoices?${billingQuery(filters)}`, {
    tenantId,
  });
}

export async function getUsage(
  year?: number,
  month?: number,
  tenantId?: string,
): Promise<UsageInfo> {
  return adminHttp.get(
    `/api/admin/billing/usage?${billingQuery({ year, month })}`,
    { tenantId },
  );
}

export async function getPlatformPaymentOptions(
  tenantId?: string,
): Promise<PlatformPaymentOptions> {
  return await adminHttp.get<PlatformPaymentOptions>(
    "/api/admin/billing/payment-options",
    { tenantId },
  );
}

export async function preparePayPalSubscription(
  planId: string,
  tenantId?: string,
): Promise<{ orderId: string; payPalPlanId: string }> {
  return await adminHttp.post(
    "/api/admin/billing/paypal/prepare",
    { planId },
    { tenantId },
  );
}

export type PayPalRevisionPreparation = {
  orderId: string;
  subscriptionId: string;
  payPalPlanId: string;
  approvalUrl: string;
};

export async function revisePayPalSubscription(
  planId: string,
  tenantId?: string,
): Promise<PayPalRevisionPreparation> {
  return await adminHttp.post(
    "/api/admin/billing/paypal/revise",
    { planId },
    { tenantId },
  );
}

export async function confirmPayPalSubscription(
  orderId: string,
  subscriptionId: string,
  tenantId?: string,
): Promise<PlatformSubscriptionResult> {
  return await adminHttp.post(
    "/api/admin/billing/paypal/confirm",
    {
      orderId,
      subscriptionId,
    },
    { tenantId },
  );
}

export async function requestCashSubscription(
  input: {
    planId: string;
    reference?: string;
    note?: string;
  },
  tenantId?: string,
): Promise<PlatformSubscriptionResult> {
  return await adminHttp.post("/api/admin/billing/cash", input, { tenantId });
}
